import React, { useState, useEffect } from 'react';
import { CARD_WIDTH_NUM, CARD_HEIGHT_NUM } from '../../lib/templates';
import { debug, warn } from '../../lib/logger';

interface TemplatePreviewComponentProps {
  previewUrl: string;
  side: 'front' | 'back';
  isLoading?: boolean;
  className?: string;
  id?: string;
}

/**
 * Component to display the rendered template preview image
 */
export function TemplatePreviewComponent({
  previewUrl,
  side,
  isLoading = false,
  className = '',
  id,
}: TemplatePreviewComponentProps) {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Reset error state when URL changes
  useEffect(() => {
    if (previewUrl) {
      setImgError(false);
      setImgLoaded(false);
    }
  }, [previewUrl]);

  // Handle image loading success
  const handleImgLoad = () => {
    debug(`Image loaded successfully for ${side} preview`);
    setImgLoaded(true);
  };

  // Handle image loading error
  const handleImgError = () => {
    warn(`Failed to load preview image for ${side}:`, previewUrl);
    setImgError(true);
  };

  // Determine if we're dealing with an SVG URL
  const isSvgUrl = previewUrl && (
    previewUrl.includes('<svg') || 
    previewUrl.startsWith('data:image/svg+xml') || 
    previewUrl.endsWith('.svg')
  );

  useEffect(() => {
    debug(`Rendering ${side} preview with URL: ${previewUrl ? `${previewUrl.substring(0, 30)}...` : 'none'}`);
  }, [previewUrl, side]);

  return (
    <div className={`border rounded bg-white shadow-sm overflow-hidden w-full ${className}`} id={id}>
      <div 
        className="relative w-full"
        style={{ 
          aspectRatio: `${CARD_WIDTH_NUM} / ${CARD_HEIGHT_NUM}`,
        }}
        data-testid="template-preview"
      >
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <p className="text-gray-500">Generating preview...</p>
          </div>
        ) : previewUrl && !imgError ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src={previewUrl}
              alt={`${side} preview`}
              className="max-w-full max-h-full object-contain"
              onLoad={handleImgLoad}
              onError={handleImgError}
            />
            {!imgLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <p className="text-gray-500">Loading preview...</p>
              </div>
            )}
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <p className="text-gray-500">
              {imgError ? "Error loading preview" : "No preview available"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 